import { CdkMenuModule, CdkMenuTrigger } from '@angular/cdk/menu';
import { ChangeDetectionStrategy, Component, inject, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { BxSpinnerModule } from '@buildxact/ui-components/bx-spinner';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    finalize,
    startWith,
    Subject,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { Uuid } from '../../../../../../../graphql';
import { SingleClickModule } from '../../../../../../core/directives/single-click.directive';
import { VisibilityObserverModule } from '../../../../../../core/directives/visibility-observer.directive';
import { BxLogger } from '../../../../../../core/services/bx-logger.service';
import { ContactsService } from '../../../../../contacts/contacts.service';
import { JobCrewContact, JobCrewContactsApiService } from '../domain/job-crew-contacts.api.service';
import { JobCrewInvitationsApiService, JobInvitation } from '../domain/job-crew-invitations.api.service';

@Component({
    selector: 'app-invite-contact',
    imports: [ReactiveFormsModule, CdkMenuModule, VisibilityObserverModule, BxSpinnerModule, SingleClickModule],
    templateUrl: './invite-contact.component.html',
    styleUrls: [
        '../../../../../../shared/composable-styles/input.scss',
        '../../../../../../shared/composable-styles/text.scss',
        '../../../../../../shared/composable-styles/dropdown-menu.scss',
        './invite-contact.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteContactComponent {
    #bsModalRef = inject(BsModalRef);
    #contactsApi = inject(JobCrewContactsApiService);
    #inviteApi = inject(JobCrewInvitationsApiService);
    #contactsService = inject(ContactsService);
    #logger = inject(BxLogger);
    #pageSize = 10;
    hasNext = signal(true);
    hasError = signal(false);
    contacts = signal<JobCrewContact[]>([]);
    selected = signal<JobCrewContact | null>(null);
    jobId: Uuid = null!;
    result = output<JobInvitation>();
    contactSearch = new FormControl('');
    updatedEmail = new FormControl('', { validators: [Validators.required, Validators.email] });
    loadNext$ = new Subject<void>();
    resultsTrigger = viewChild.required<CdkMenuTrigger>('resultsTrigger');
    sending = signal(false);

    constructor() {
        const search = this.contactSearch.valueChanges.pipe(
            takeUntilDestroyed(),
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => {
                this.contacts.set([]);
                this.#openResults();
            }),
            startWith('')
        );
        combineLatest([search, this.loadNext$])
            .pipe(
                takeUntilDestroyed(),
                switchMap(([text]) => this.#contactsApi.searchContacts(text ?? '', this.contacts().length, this.#pageSize))
            )
            .subscribe((res) => {
                const contacts = res.data.contacts;
                if (!contacts?.items) return this.#logger.error('Something went wrong');

                this.hasNext.set(contacts.pageInfo.hasNextPage);
                this.contacts.set([...this.contacts(), ...contacts.items]);
            });
    }

    selectContact(contact: JobCrewContact) {
        this.selected.set(contact);
        this.#closeResults();
    }

    deselectContact() {
        this.selected.set(null);
        this.updatedEmail.reset();
    }

    async createContact() {
        try {
            const newContact = await this.#contactsService.newContact({ preventOpen: true }, true);

            this.selected.set({
                iD_Contact: newContact.ID_Contact,
                name: newContact.Name,
                businessName: newContact.BusinessName,
                email: newContact.Email,
            });
        } catch {}
    }

    sendInvitation() {
        const selectedContact = this.selected();
        if (!selectedContact || (!selectedContact.email && this.updatedEmail.invalid)) return;

        this.sending.set(true);

        if (!selectedContact.email) {
            return this.#contactsApi
                .updateContactEmail(selectedContact.iD_Contact, this.updatedEmail.value!)
                .pipe(
                    take(1),
                    catchError(() => {
                        this.#logger.error('Something went wrong');
                        return EMPTY;
                    })
                )
                .subscribe((res) => {
                    if (!res.data?.updateContacts.contact) {
                        this.sending.set(false);
                        return this.#logger.error('Something went wrong');
                    }

                    this.#logger.success('Contact updated');
                    this.#handleInvitation(selectedContact);
                });
        }

        this.#handleInvitation(selectedContact);
    }

    #handleInvitation(selectedContact: JobCrewContact) {
        this.#inviteApi
            .createInvitation(this.jobId, selectedContact.iD_Contact)
            .pipe(
                take(1),
                finalize(() => this.sending.set(false)),
                catchError(() => {
                    this.#logger.error('Something went wrong');
                    return EMPTY;
                })
            )
            .subscribe((res) => {
                if (!res.data?.sendJobInvitationToContact.jobInvitation) return this.#logger.error('Something went wrong');

                this.#logger.success('Invitation sent');
                this.#bsModalRef.hide();
                this.result.emit(res.data.sendJobInvitationToContact.jobInvitation);
            });
    }

    #openResults() {
        if (!this.resultsTrigger().isOpen()) this.resultsTrigger().open();
    }

    #closeResults() {
        this.resultsTrigger().close();
    }

    cancel() {
        this.#bsModalRef.hide();
    }
}
